"use client";
import React, { useEffect, useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Container,
  Box,
  Badge,
  Link,
  useTheme,
  useMediaQuery,
  Popover,
  Paper,
  List,
  ListItem,
  ListItemText,
  Snackbar,
  Alert,
  AlertColor
} from '@mui/material';
import {
  ListAlt,
  Key,
  Reviews,
  TableChart,
  Create,
  FactCheck,
  ViewList,
  Logout,
  CalendarMonth,
  HowToReg,
  AccountCircle,
  EditNote,
  Dns,
  AutoStories,
  UploadFile,
  Dataset,
  PlaylistRemove
} from '@mui/icons-material';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsIcon from '@mui/icons-material/Notifications';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import GroupIcon from '@mui/icons-material/Group';
import { useConfig } from './ConfigProvider';
import Image from 'next/image';
import { useRouter, usePathname } from 'next/navigation';
import ModalBox from './formrenderer/ModalBox';
import designNewFormParams from '../layouts/DesignNewFormParams.json';
import newApplication from '../layouts/NewApplication.json';
import deleteForm from '../layouts/DeleteForm.json';
import { useAuth } from './AuthProvider';
import { useFormData } from './FormDataProvider';
import { get } from './formrenderer/utils/Utils';

const alertColors: AlertColor[] = ['success', 'info', 'warning', 'error'];

const Header: React.FC = () => {
  const [adminAnchorEl, setAdminAnchorEl] = React.useState<null | HTMLElement>(null);
  const adminMenuOpen = Boolean(adminAnchorEl);
  const [userAnchorEl, setUserAnchorEl] = React.useState<null | HTMLElement>(null);
  const userMenuOpen = Boolean(userAnchorEl);
  const [mobileMenuAnchorEl, setMobileMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const mobileMenuOpen = Boolean(mobileMenuAnchorEl);
  const [newLayoutOpen, setNewLayoutOpen] = React.useState<Boolean>(false);
  const [newFormOpen, setNewFormOpen] = React.useState<Boolean>(false);
  const [deleteFormOpen, setDeleteFormOpen] = React.useState<Boolean>(false);

  const { appName, apiUrl, loginUrl } = useConfig();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const pathname = usePathname();
  const { account, logout, isAdmin, isInRole, registrationError } = useAuth();
  const { formData, navigatePage } = useFormData();
  const [validationMessage, setValidationMessage] = useState<string | null>(null);
  const [validationSeverity, setValidationSeverity] = useState<AlertColor>("error");
  const [popoverAnchorEl, setPopoverAnchorEl] = useState<null | HTMLElement>(null);
  const isPopoverOpen = Boolean(popoverAnchorEl);

  const validationErrors = get(formData, "validationResult_errors");
  const errors = Object.values(validationErrors ?? {});

  const isUserAdmin = isAdmin();
  const isDeveloper = isInRole("eForm_Developer");

  const isRegistrationError = !!registrationError;

  const handleAdminMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAdminAnchorEl(event.currentTarget);
  };

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAdminAnchorEl(null);
    setUserAnchorEl(null);
    setMobileMenuAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleMenuClose();
  };

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMenuAnchorEl(event.currentTarget);
  };

  const handleNavigation = (path: string) => {
    navigatePage(path);
    handleMenuClose();
  };

  const handleCloseSnackbar = () => {
    setValidationMessage(null);
  };

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (errors.length > 0) {
      setPopoverAnchorEl(event.currentTarget);
    }
  };

  const handlePopoverClose = () => {
    setPopoverAnchorEl(null);
  };

  // Effect to close modals on route change
  useEffect(() => {
    setNewLayoutOpen(false);
    setNewFormOpen(false);
    setDeleteFormOpen(false);
  }, [pathname]); // Only run on pathname change

  useEffect(() => {
    const resultMessage = get(formData, "validationResult_resultMessage", null)
    if (resultMessage) {
      setValidationMessage(errors.length > 0 ? `${resultMessage}. See the error list at the notification menu on the top.` : resultMessage);
      const severity = get(formData, "validationResult_severity", 3);
      if (severity >= 0 && severity <= 3) {
        setValidationSeverity(alertColors[severity]);
      }
    }
  }, [formData, errors.length]);

  return (
    <AppBar position="static">
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          <IconButton
            size="large"
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={handleMobileMenuOpen}
            sx={{ display: isMobile ? 'flex' : 'none' }} // Conditionally display on narrow screens
          >
            <MenuIcon />
          </IconButton>

          <Link href="http://rbdmsonline.org">
            <Image src="/rbdms-logo.svg" alt="RBDMS Logo" width={180} height={160} priority className="eform-header-img" />
          </Link>

          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {appName}
          </Typography>

          {account && !isMobile && !isRegistrationError && (
            <>
              <Button color="inherit" onClick={() => handleNavigation('/reports')}>Reports</Button>
              <Button color="inherit" onClick={() => setNewFormOpen(true)}>New Application</Button>
              <Button color="inherit" onClick={() => handleNavigation('/datadict')}>Data Dictionaries</Button>
              <Button color="inherit" onClick={() => handleNavigation('/reviewtasks')}>Review Tasks</Button>
              {isUserAdmin && (
                <>
                  <Button
                    id="demo-customized-button"
                    aria-controls={adminMenuOpen ? 'admin-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={adminMenuOpen ? 'true' : undefined}
                    variant="contained"
                    disableElevation
                    onClick={handleAdminMenuOpen}
                    endIcon={<KeyboardArrowDownIcon />}
                  >
                    Admin
                  </Button>
                  <Menu
                    id="admin-menu"
                    disableScrollLock
                    className="eform-admin-menu"
                    anchorEl={adminAnchorEl}
                    open={adminMenuOpen}
                    onClose={handleMenuClose}
                    onClick={handleMenuClose}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                  >
                    <Typography variant="subtitle1" color="primary" className="eform-menu-header">Administrative Forms</Typography>
                    <MenuItem onClick={() => handleNavigation('/admin/user-permissions')}>
                      <GroupIcon className="eform-menu-icon" />
                      <Typography variant="inherit">User Permissions</Typography>
                    </MenuItem>
                    <MenuItem onClick={() => setNewLayoutOpen(true)}>
                      <EditNote className="eform-menu-icon" />
                      <Typography variant="inherit">Design New Form</Typography>
                    </MenuItem>
                    <MenuItem onClick={() => setDeleteFormOpen(true)}>
                      <PlaylistRemove className="eform-menu-icon" />
                      <Typography variant="inherit">Delete Form</Typography>
                    </MenuItem>
                    <MenuItem onClick={() => handleNavigation('/admin/edit-lookups')}>
                      <Dns className="eform-menu-icon" />
                      <Typography variant="inherit">Edit Lookups</Typography>
                    </MenuItem>
                    <MenuItem onClick={() => handleNavigation('/admin/edit-datatables')}>
                      <Dataset className="eform-menu-icon" />
                      <Typography variant="inherit">Edit Form Data Tables</Typography>
                    </MenuItem>
                    <MenuItem onClick={() => handleNavigation('/admin/edit-pages')}>
                      <AutoStories className="eform-menu-icon" />
                      <Typography variant="inherit">Edit Pages</Typography>
                    </MenuItem>
                    <MenuItem onClick={() => handleNavigation('/admin/upload-documents')}>
                      <UploadFile className="eform-menu-icon" />
                      <Typography variant="inherit">Upload Documents</Typography>
                    </MenuItem>
                    <Typography variant="subtitle1" color="primary" className="eform-menu-header">Sampling Data</Typography>
                    <MenuItem onClick={() => handleNavigation('/admin/sample-report-schedules')}>
                      <CalendarMonth className="eform-menu-icon" />
                      <Typography variant="inherit">Sample Report Schedules</Typography>
                    </MenuItem>
                    <MenuItem onClick={() => handleNavigation('/admin/list-parameters')}>
                      <ViewList className="eform-menu-icon" />
                      <Typography variant="inherit">List Parameters</Typography>
                    </MenuItem>
                    <MenuItem onClick={() => handleNavigation('/admin/list-result-methods')}>
                      <FactCheck className="eform-menu-icon" />
                      <Typography variant="inherit">List Result Methods</Typography>
                    </MenuItem>
                    <Typography variant="subtitle1" color="primary" className="eform-menu-header">Users</Typography>
                    <MenuItem onClick={() => window.open(`${loginUrl}/User`, '_blank')}>
                      <HowToReg className="eform-menu-icon" />
                      <Typography variant="inherit">New Registrations</Typography>
                    </MenuItem>
                    {isDeveloper && <>
                      <Typography variant="subtitle1" color="primary" className="eform-menu-header">Development</Typography>
                      <MenuItem onClick={() => handleNavigation('/admin/change-status-actions')}>
                        <Typography variant="inherit">Change Status Actions</Typography>
                      </MenuItem>
                      <MenuItem onClick={() => handleNavigation('/admin/edd-mapping')}>
                        <Typography variant="inherit">EDD Mapping</Typography>
                      </MenuItem>
                      <MenuItem onClick={() => handleNavigation('/admin/edd-translator-download')}>
                        <Typography variant="inherit">EDD Translator Download</Typography>
                      </MenuItem>
                      <MenuItem onClick={() => handleNavigation('/admin/edd-translator-upload')}>
                        <Typography variant="inherit">EDD Translator Upload</Typography>
                      </MenuItem>
                      <MenuItem onClick={() => window.open(`${apiUrl}/Admin/logFile`, '_blank')}>
                        <Typography variant="inherit">Server Log</Typography>
                      </MenuItem>
                    </>}
                  </Menu>
                </>
              )}
            </>
          )}

          {account && !isRegistrationError && (<>
            <IconButton
              size="large"
              aria-label="show validation errors"
              color="inherit"
              onClick={handlePopoverOpen}
            >
              <Badge badgeContent={errors.length} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>

            <Typography
              variant="inherit"
              sx={{ ml: 2 }}
            >
              {account.name}
            </Typography>

            <IconButton
              size="large"
              edge="end"
              color="inherit"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleUserMenuOpen}
            >
              <AccountCircle />
            </IconButton>

            <Menu
              id="user-menu"
              disableScrollLock
              anchorEl={userAnchorEl}
              open={userMenuOpen}
              onClose={handleMenuClose}
              onClick={handleMenuClose}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              {/* <MenuItem onClick={handleMenuClose}>
                <Key className="eform-menu-icon" />
                <Typography variant="inherit">Change Password</Typography>
              </MenuItem> */}
              <MenuItem onClick={handleLogout}>
                <Logout className="eform-menu-icon" />
                <Typography variant="inherit">Logout</Typography>
              </MenuItem>
            </Menu>

            <Menu
              id="mobile-menu"
              disableScrollLock
              anchorEl={mobileMenuAnchorEl}
              open={mobileMenuOpen}
              onClose={handleMenuClose}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              <MenuItem onClick={() => handleNavigation('/reports')}>
                <ListAlt className="eform-menu-icon" />
                <Typography variant="inherit">Reports</Typography>
              </MenuItem>
              <MenuItem onClick={() => setNewFormOpen(true)}>
                <Create className="eform-menu-icon" />
                <Typography variant="inherit">New Application</Typography>
              </MenuItem>
              <MenuItem onClick={() => handleNavigation('/datadict')}>
                <TableChart className="eform-menu-icon" />
                <Typography variant="inherit">Data Dictionaries</Typography>
              </MenuItem>
              <MenuItem onClick={() => handleNavigation('/reviewtasks')}>
                <Reviews className="eform-menu-icon" />
                <Typography variant="inherit">Review Tasks</Typography>
              </MenuItem>
              {isUserAdmin && (
                <MenuItem onClick={handleAdminMenuOpen}>
                  <Typography variant="inherit">Admin</Typography>
                  <KeyboardArrowDownIcon />
                </MenuItem>
              )}
              {isUserAdmin && (
                <Menu
                  id="admin-menu-mobile"
                  disableScrollLock
                  anchorEl={adminAnchorEl}
                  open={adminMenuOpen}
                  onClose={handleMenuClose}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                >
                  <Typography variant="subtitle1" color="primary" className="eform-menu-header">Administrative Forms</Typography>
                  <MenuItem onClick={() => handleNavigation('/admin/user-permissions')}>
                    <GroupIcon className="eform-menu-icon" />
                    <Typography variant="inherit">User Permissions</Typography>
                  </MenuItem>
                  <MenuItem onClick={() => setNewLayoutOpen(true)}>
                    <EditNote className="eform-menu-icon" />
                    <Typography variant="inherit">Design New Form</Typography>
                  </MenuItem>
                  <MenuItem onClick={() => setDeleteFormOpen(true)}>
                    <PlaylistRemove className="eform-menu-icon" />
                    <Typography variant="inherit">Delete Form</Typography>
                  </MenuItem>
                  <MenuItem onClick={() => handleNavigation('/admin/edit-lookups')}>
                    <Dns className="eform-menu-icon" />
                    <Typography variant="inherit">Edit Lookups</Typography>
                  </MenuItem>
                  <MenuItem onClick={() => handleNavigation('/admin/edit-datatables')}>
                    <Dataset className="eform-menu-icon" />
                    <Typography variant="inherit">Edit Form Data Tables</Typography>
                  </MenuItem>
                  <MenuItem onClick={() => handleNavigation('/admin/edit-pages')}>
                    <AutoStories className="eform-menu-icon" />
                    <Typography variant="inherit">Edit Pages</Typography>
                  </MenuItem>
                  <MenuItem onClick={() => handleNavigation('/admin/upload-documents')}>
                    <UploadFile className="eform-menu-icon" />
                    <Typography variant="inherit">Upload Documents</Typography>
                  </MenuItem>
                  <Typography variant="subtitle1" color="primary" className="eform-menu-header">Sampling Data</Typography>
                  <MenuItem onClick={() => handleNavigation('/admin/sample-report-schedules')}>
                    <CalendarMonth className="eform-menu-icon" />
                    <Typography variant="inherit">Sample Report Schedules</Typography>
                  </MenuItem>
                  <MenuItem onClick={() => handleNavigation('/admin/list-parameters')}>
                    <ViewList className="eform-menu-icon" />
                    <Typography variant="inherit">List Parameters</Typography>
                  </MenuItem>
                  <MenuItem onClick={() => handleNavigation('/admin/list-result-methods')}>
                    <FactCheck className="eform-menu-icon" />
                    <Typography variant="inherit">List Result Methods</Typography>
                  </MenuItem>
                  <Typography variant="subtitle1" color="primary" className="eform-menu-header">Users</Typography>
                  <MenuItem onClick={() => window.open(`${loginUrl}/User`, '_blank')}>
                    <HowToReg className="eform-menu-icon" />
                    <Typography variant="inherit">New Registrations</Typography>
                  </MenuItem>
                  {isDeveloper && <>
                    <Typography variant="subtitle1" color="primary" className="eform-menu-header">Development</Typography>
                    <MenuItem onClick={() => handleNavigation('/admin/change-status-actions')}>
                        <Typography variant="inherit">Change Status Actions</Typography>
                      </MenuItem>
                    <MenuItem onClick={() => handleNavigation('/admin/edd-mapping')}>
                      <Typography variant="inherit">EDD Mapping</Typography>
                    </MenuItem>
                    <MenuItem onClick={() => handleNavigation('/admin/edd-translator-download')}>
                      <Typography variant="inherit">EDD Translator Download</Typography>
                    </MenuItem>
                    <MenuItem onClick={() => handleNavigation('/admin/edd-translator-upload')}>
                      <Typography variant="inherit">EDD Translator Upload</Typography>
                    </MenuItem>
                    <MenuItem onClick={() => window.open(`${apiUrl}/Admin/logFile`, '_blank')}>
                      <Typography variant="inherit">Server Log</Typography>
                    </MenuItem>
                  </>}
                </Menu>
              )}
              <MenuItem onClick={handleMenuClose}>
                <Key className="eform-menu-icon" />
                <Typography variant="inherit">Change Password</Typography>
              </MenuItem>
              <MenuItem onClick={handleMenuClose}>
                <Logout className="eform-menu-icon" />
                <Typography variant="inherit">Logout</Typography>
              </MenuItem>
            </Menu>
          </>
          )}
        </Toolbar>
      </Container>
      <Box
        sx={{
          width: '100%',
          height: '4px',
          backgroundColor: 'orange',
        }}
      />
      {newLayoutOpen &&
        <ModalBox
          action={{
            type: 'messagebox',
            payload: {
              title: 'Create New Layout',
              layout: designNewFormParams,
              cancelbutton: true
            },
            successAction: {
              type: "postdata2",
              payload: {
                endpoint: "Form/newlayout",
                dataKey: "FormKey"
              },
              successAction: {
                type: "fetchlookupdata",
                payload: {
                  dataKey: "formTypeReport"
                },
                successAction: {
                  type: "loadform",
                  payload: {
                    dataKey: "FormKey",
                    formMode: "design"
                  }
                },
                failureAction: {
                  type: "messagebox",
                  payload: {
                    message: "Failed to load form types!",
                    closebutton: true
                  }
                }
              },
              failureAction: {
                type: "messagebox",
                payload: {
                  message: "Failed to create layout!",
                  closebutton: true
                }
              }
            }
          }}
          data={{}}
          onDataChange={() => { }}
          mode="edit"
          onComplete={() => { setNewLayoutOpen(false) }}
          onError={() => { setNewLayoutOpen(false) }}
          onClose={() => { setNewLayoutOpen(false) }}
          sx={{ minWidth: '300px' }}
        />}
      {newFormOpen &&
        <ModalBox
          action={{
            type: 'messagebox',
            payload: {
              title: 'Create New Form',
              layout: newApplication,
              cancelbutton: true
            },
            successAction: {
              type: "postdata2",
              payload: {
                endpoint: "Form/new",
                dataKey: "FormKey"
              },
              successAction: {
                type: "loadform",
                payload: {
                  dataKey: "FormKey",
                  formMode: "edit"
                }
              },
              failureAction: {
                type: "messagebox",
                payload: {
                  message: "Failed to create form!",
                  closebutton: true
                }
              }
            }
          }}
          data={{}}
          onDataChange={() => { }}
          mode="edit"
          onComplete={() => { setNewFormOpen(false) }}
          onError={() => { setNewFormOpen(false) }}
          onClose={() => { setNewFormOpen(false) }}
          sx={{ minWidth: '300px' }}
        />}
      {deleteFormOpen &&
        <ModalBox
          action={{
            type: 'messagebox',
            payload: {
              title: 'Delete Form',
              layout: deleteForm,
              cancelbutton: true
            },
            successAction: {
              type: "messagebox",
              payload: {
                message: "Do you want to delete Form ID: ${FormKey}?",
                okbutton: true,
                cancelbutton: true
              },
              successAction: {
                type: "postdata",
                payload: {
                  endpoint: "Form/delete",
                  dataKey: "FormKey"
                },
                successAction: {
                  type: "messagebox",
                  payload: {
                    message: "Form with ID: ${FormKey} deleted successfully!",
                    closebutton: true
                  }
                },
                failureAction: {
                  type: "messagebox",
                  payload: {
                    message: "Failed to delete form!",
                    closebutton: true
                  }
                }
              }
            },
          }
          }
          data={{}}
          onDataChange={() => { }}
          mode="edit"
          onComplete={() => { setDeleteFormOpen(false) }}
          onError={() => { setDeleteFormOpen(false) }}
          onClose={() => { setDeleteFormOpen(false) }}
          sx={{ minWidth: '300px' }}
        />}
      <Snackbar
        open={!!validationMessage}
        //anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={validationSeverity} sx={{ width: '100%' }}>
          {validationMessage}
        </Alert>
      </Snackbar>
      <Popover
        open={isPopoverOpen}
        anchorEl={popoverAnchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        slotProps={{
          paper: {
            style: { marginTop: 20, padding: '0px', maxWidth: '300px' }, // Margin to create space and other styles
          },
        }}
      >
        <Paper style={{ padding: '0px 5px', maxWidth: '300px' }}>
          <Typography variant="h6" gutterBottom>Error Details</Typography>
          <List dense>
            {errors.map((error, index) => (
              <ListItem
                key={index}
                style={{
                  //backgroundColor: index % 2 === 0 ? '#f5f5f5' : '#ffffff', // Alternating colors
                  borderBottom: '1px solid orange', // Separator line
                }}
              >
                <ListItemText primary={error as string} />
              </ListItem>
            ))}
          </List>
        </Paper>
      </Popover>
    </AppBar>
  );
};

export default Header;
